import { useState } from 'react';

interface ProductGalleryProps {
  images: string[];
  name: string;
  badge: string | null;
}

export default function ProductGallery({ images, name, badge }: ProductGalleryProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const prev = () => setActiveIndex((i) => (i === 0 ? images.length - 1 : i - 1));
  const next = () => setActiveIndex((i) => (i === images.length - 1 ? 0 : i + 1));

  const waLink = `https://wa.me/554891802091?text=Ol%C3%A1!%20Tenho%20interesse%20no%20${encodeURIComponent(name)}.%20Poderia%20me%20passar%20mais%20informa%C3%A7%C3%B5es%3F`;

  return (
    <div className="lg:sticky lg:top-8">
      <div className="flex flex-col gap-4">
        <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl overflow-hidden flex items-center justify-center min-h-[420px]">
          {badge && (
            <span className="absolute top-5 left-5 z-10 bg-green-500 text-white text-xs font-black px-3 py-1.5 rounded-full uppercase tracking-wide">
              {badge}
            </span>
          )}
          <button
            className="absolute top-5 right-5 z-10 w-9 h-9 flex items-center justify-center bg-white/80 hover:bg-white rounded-full transition-all cursor-pointer"
            aria-label="Ampliar imagem"
            onClick={() => setLightboxOpen(true)}
          >
            <i className="ri-zoom-in-line text-gray-700 text-base" />
          </button>
          <div
            className="w-full h-[420px] flex items-center justify-center p-10 cursor-zoom-in"
            onClick={() => setLightboxOpen(true)}
          >
            <img
              alt={`${name} - máquina de solda de geomembrana - foto ${activeIndex + 1}`}
              className="w-full max-w-sm h-80 object-contain transition-opacity duration-300"
              src={images[activeIndex]}
            />
          </div>
          {images.length > 1 && (
            <>
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-white/80 hover:bg-white rounded-full transition-all cursor-pointer"
                aria-label="Foto anterior"
                onClick={prev}
              >
                <i className="ri-arrow-left-s-line text-gray-700 text-lg" />
              </button>
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-white/80 hover:bg-white rounded-full transition-all cursor-pointer"
                aria-label="Próxima foto"
                onClick={next}
              >
                <i className="ri-arrow-right-s-line text-gray-700 text-lg" />
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
                {images.map((_, i) => (
                  <button
                    key={i}
                    className={`rounded-full transition-all cursor-pointer ${i === activeIndex ? 'w-5 h-2 bg-green-500' : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'}`}
                    aria-label={`Ir para foto ${i + 1}`}
                    onClick={() => setActiveIndex(i)}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {images.length > 1 && (
          <div className="flex gap-3">
            {images.map((img, i) => (
              <button
                key={i}
                className={`flex-1 h-20 rounded-xl overflow-hidden border-2 transition-all cursor-pointer bg-gray-50 flex items-center justify-center ${i === activeIndex ? 'border-green-500' : 'border-transparent hover:border-gray-200'}`}
                aria-label={`Ver foto ${i + 1}`}
                onClick={() => setActiveIndex(i)}
              >
                <img alt={`${name} - máquina de solda de geomembrana - miniatura ${i + 1}`} className="w-full h-full object-contain p-1" src={img} loading="lazy" decoding="async" />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6">
        <a
          href={waLink}
          target="_blank"
          rel="nofollow noreferrer"
          className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-black text-sm px-5 py-3.5 rounded-xl transition-all cursor-pointer whitespace-nowrap uppercase tracking-wide"
        >
          <i className="ri-whatsapp-line" />
          WhatsApp
        </a>
      </div>

      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white cursor-pointer"
            onClick={() => setLightboxOpen(false)}
          >
            <i className="ri-close-line text-xl" />
          </button>
          <img
            alt={`${name} — máquina de solda geomembrana`}
            title={`${name} — máquina de solda geomembrana`}
            className="max-w-full max-h-full object-contain"
            src={images[activeIndex]}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
